/**
 * Keeps Searchanise split-by-Length product cards linked to the Shopify
 * variant represented by the card.
 *
 * Debug with: ?snizeVariantDebug=1
 */
(function () {
  'use strict';

  var ROOT_SELECTOR =
    '.snize-search-results, .snize-results-page, #snize-search-results-grid-mode, #snize-search-results-list-mode, .snize-ac-results';
  var CARD_SELECTOR = '.snize-product';
  var PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';
  var DEBUG_PARAM = 'snizeVariantDebug';
  var DEBOUNCE_MS = 80;

  var pagePath = window.location.pathname.replace(/\/$/, '');
  var resultsPageEligible =
    /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+(?:\/[^/]+)?$/i.test(pagePath) ||
    /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search$/i.test(pagePath) ||
    /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/search-results-page$/i.test(pagePath);

  var debugEnabled = new URLSearchParams(window.location.search).get(DEBUG_PARAM) === '1';
  var productCache = new Map();
  var cardStates = new WeakMap();
  var observedRoots = new WeakSet();
  var responsiveImages = new Set();
  var imageResizeObserver = null;
  var imageResizeFrame = null;
  var hoverRepairPending = new WeakSet();
  var apiRecordsByHandle = new Map();
  var timer = null;
  var pendingScopes = new Set();
  var searchaniseEventsBound = false;

  function debug() {
    if (!debugEnabled || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[Searchanise variant fix]');
    window.console.info.apply(window.console, args);
  }

  function normalize(value) {
    return String(value || '')
      .replace(/[\u201c\u201d\u201e\u201f\u2033\u02ba]/g, '"')
      .replace(/[\u2018\u2019\u2032]/g, "'")
      .replace(/\u00a0/g, ' ')
      .replace(/(\d)\s*(?:inches|inch|in\.)\b/gi, '$1"')
      .replace(/'{2}/g, '"')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  function unique(values) {
    return Array.from(new Set(values.filter(Boolean)));
  }

  function handleFromUrl(value) {
    if (!value) return '';

    try {
      var url = new URL(value, window.location.origin);
      var match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]).toLowerCase() : '';
    } catch (error) {
      return '';
    }
  }

  function getCardHandle(card) {
    var links = card.querySelectorAll(PRODUCT_LINK_SELECTOR);
    for (var index = 0; index < links.length; index += 1) {
      var handle = handleFromUrl(links[index].getAttribute('href'));
      if (handle) return handle;
    }

    var possibleUrls = card.querySelectorAll('[data-url], [data-href], [data-product-url]');
    for (var dataIndex = 0; dataIndex < possibleUrls.length; dataIndex += 1) {
      var element = possibleUrls[dataIndex];
      var dataHandle = handleFromUrl(
        element.getAttribute('data-url') ||
          element.getAttribute('data-href') ||
          element.getAttribute('data-product-url')
      );
      if (dataHandle) return dataHandle;
    }

    return '';
  }

  function optionName(option) {
    return typeof option === 'string' ? option : option && option.name;
  }

  function variantOptions(variant) {
    if (Array.isArray(variant.options)) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter(function (value) {
      return typeof value !== 'undefined' && value !== null;
    });
  }

  function findLengthIndex(product) {
    return (product.options || []).findIndex(function (option) {
      return normalize(optionName(option)) === 'length';
    });
  }

  function getProduct(handle) {
    if (productCache.has(handle)) {
      debug('product JSON cache hit', handle);
      return productCache.get(handle);
    }

    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    if (root.slice(-1) !== '/') root += '/';

    var request = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Product JSON request returned ' + response.status);
      }
      return response.json();
    });

    productCache.set(handle, request);
    debug('product JSON cache miss', handle);

    request.catch(function () {
      productCache.delete(handle);
    });

    return request;
  }

  function textFromElement(element) {
    if (!element) return '';
    return (
      element.getAttribute('data-length') ||
      element.getAttribute('data-option-length') ||
      element.getAttribute('data-variant-title') ||
      element.getAttribute('data-product-title') ||
      element.getAttribute('aria-label') ||
      element.getAttribute('title') ||
      element.textContent ||
      ''
    );
  }

  function intentSources(card) {
    var sources = [];
    var explicit = card.querySelectorAll(
      '[data-length], [data-option-length], [data-variant-title], [data-product-title]'
    );
    explicit.forEach(function (element) {
      sources.push(textFromElement(element));
    });

    [
      '.snize-title',
      '.snize-product-title',
      '.snize-item-title',
      '[class*="product-title"]',
      'h2',
      'h3'
    ].forEach(function (selector) {
      card.querySelectorAll(selector).forEach(function (element) {
        sources.push(textFromElement(element));
      });
    });

    card.querySelectorAll('img[alt]').forEach(function (image) {
      sources.push(image.getAttribute('alt'));
    });

    sources.push(card.textContent || '');
    return unique(sources.map(normalize));
  }

  function sourceContainsOption(source, optionValue) {
    if (!source || !optionValue) return false;
    if (source === optionValue) return true;

    var escaped = optionValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('(^|[\\s|,;:/()\\[\\]\\-])' + escaped + '(?=$|[\\s|,;:/()\\[\\]\\-])').test(
      source
    );
  }

  function detectLength(card, product, lengthIndex) {
    var valueMap = new Map();

    (product.variants || []).forEach(function (variant) {
      var value = variantOptions(variant)[lengthIndex];
      var normalizedValue = normalize(value);
      if (!normalizedValue) return;
      if (!valueMap.has(normalizedValue)) {
        valueMap.set(normalizedValue, { display: value, variants: [] });
      }
      valueMap.get(normalizedValue).variants.push(variant);
    });

    var sources = intentSources(card);
    for (var sourceIndex = 0; sourceIndex < sources.length; sourceIndex += 1) {
      var source = sources[sourceIndex];
      var matches = [];
      valueMap.forEach(function (entry, normalizedValue) {
        if (sourceContainsOption(source, normalizedValue)) matches.push(entry);
      });

      if (matches.length === 1) {
        return {
          display: matches[0].display,
          normalized: normalize(matches[0].display),
          variants: matches[0].variants,
          source: source
        };
      }
    }

    return null;
  }

  function numericId(value) {
    var match = String(value || '').match(/(\d{6,})\s*$/);
    return match ? match[1] : '';
  }

  function domVariantIds(card) {
    var candidates = [];
    var attributeNames = [
      'data-selected-variant-id',
      'data-variant-id',
      'data-variant_id',
      'data-add-to-cart-id'
    ];
    var preferred = [card];

    card
      .querySelectorAll(
        'a[data-variant-id], [data-selected-variant-id], [aria-selected="true"][data-variant-id], .selected[data-variant-id], .active[data-variant-id]'
      )
      .forEach(function (element) {
        preferred.push(element);
      });

    preferred.forEach(function (element) {
      attributeNames.forEach(function (attribute) {
        var id = numericId(element.getAttribute && element.getAttribute(attribute));
        if (id) candidates.push(id);
      });
    });

    if (!candidates.length) {
      var allVariantElements = card.querySelectorAll('[data-variant-id]');
      var allIds = unique(
        Array.from(allVariantElements).map(function (element) {
          return numericId(element.getAttribute('data-variant-id'));
        })
      );
      if (allIds.length === 1) candidates.push(allIds[0]);
    }

    return unique(candidates);
  }

  function apiVariantIds(handle, card) {
    var records = apiRecordsByHandle.get(handle) || [];
    if (!records.length) return [];

    var cardSources = intentSources(card);
    var matching = records.filter(function (record) {
      var title = normalize(record.title);
      return !title || cardSources.some(function (source) {
        return source === title || source.indexOf(title) !== -1 || title.indexOf(source) !== -1;
      });
    });
    var usable = matching.length ? matching : records.length === 1 ? records : [];

    return unique(
      usable.map(function (record) {
        return numericId(record.variantId);
      })
    );
  }

  function chooseVariant(card, handle, product, lengthIndex, lengthMatch) {
    var variants = product.variants || [];
    var directIds = unique(domVariantIds(card).concat(apiVariantIds(handle, card)));
    var directVariants = directIds
      .map(function (id) {
        return variants.find(function (variant) {
          return String(variant.id) === String(id);
        });
      })
      .filter(Boolean);

    if (lengthMatch) {
      var directLengthMatch = directVariants.find(function (variant) {
        return normalize(variantOptions(variant)[lengthIndex]) === lengthMatch.normalized;
      });
      if (directLengthMatch) {
        return { variant: directLengthMatch, strategy: 'Searchanise variant ID + Length validation' };
      }

      var availableMatch = lengthMatch.variants.find(function (variant) {
        return variant.available !== false;
      });
      return {
        variant: availableMatch || lengthMatch.variants[0],
        strategy: 'rendered Length + product JSON'
      };
    }

    if (directVariants.length === 1) {
      return { variant: directVariants[0], strategy: 'Searchanise variant ID' };
    }

    return null;
  }

  function variantImageUrl(variant) {
    var image = variant && variant.featured_image;
    if (typeof image === 'string') return image;
    if (image && (image.src || image.url)) return image.src || image.url;

    var preview = variant && variant.featured_media && variant.featured_media.preview_image;
    return preview && (preview.src || preview.url) ? preview.src || preview.url : '';
  }

  function imageUrlAtWidth(source, width) {
    try {
      var url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return source;
    }
  }

  function imageSrcset(source) {
    return [360, 540, 720, 900, 1200]
      .map(function (width) {
        return imageUrlAtWidth(source, width) + ' ' + width + 'w';
      })
      .join(', ');
  }

  // T6 (2026-09-24): width descriptors need the actual card slot, not the
  // browser's default 100vw. Observe only variant images this script owns.
  function setImageSizes(image, width) {
    if (!(width > 0)) return; // Hidden cards keep their last usable hint.
    var sizes = Math.ceil(width) + 'px';
    var targets = [image];
    var picture = image.closest('picture');
    if (picture) targets = targets.concat(Array.from(picture.querySelectorAll('source')));
    // Fill the slot like default-sized neighbours: a 1200w descriptor on a 1000px original
    // would otherwise draw ~17% smaller than the cards around it.
    if (image.style.width !== '100%') {
      image.style.width = '100%';
      image.style.height = 'auto';
      image.style.objectFit = 'contain';
    }
    targets.forEach(function (target) {
      if (target.getAttribute('sizes') !== sizes) target.setAttribute('sizes', sizes);
      if (target.hasAttribute('data-sizes') && target.getAttribute('data-sizes') !== sizes) {
        target.setAttribute('data-sizes', sizes);
      }
    });
  }

  // 2026-09-30: measure the card slot, never the img. The img has no CSS width, so its
  // rendered width follows `sizes`; feeding it back made a feedback loop (a transient 5px
  // during the variant swap -> sizes="5px" -> 360w drawn at 5px -> permanent 4x4 "dot" on
  // live Grace cards).
  function slotWidth(image) {
    var slot = image.closest('.snize-thumbnail') || image.parentElement;
    return (slot || image).getBoundingClientRect().width;
  }

  function pruneResponsiveImages() {
    responsiveImages.forEach(function (image) {
      if (image.isConnected) return;
      if (imageResizeObserver) imageResizeObserver.unobserve(image);
      responsiveImages.delete(image);
    });
  }

  function trackImageSizes(image) {
    if (responsiveImages.has(image)) {
      // A Searchanise rerender can replace picture sources without resizing the img.
      var knownSize = image.getAttribute('sizes') || '';
      if (/^\d+px$/.test(knownSize)) setImageSizes(image, slotWidth(image));
      return;
    }
    responsiveImages.add(image);
    setImageSizes(image, slotWidth(image));
    if (window.ResizeObserver) {
      if (!imageResizeObserver) {
        imageResizeObserver = new ResizeObserver(function (entries) {
          pruneResponsiveImages();
          entries.forEach(function (entry) {
            if (entry.target.isConnected) setImageSizes(entry.target, slotWidth(entry.target));
          });
        });
      }
      imageResizeObserver.observe(image);
    }
  }

  // Fallback for browsers without ResizeObserver; batch geometry reads before writes.
  window.addEventListener('resize', function () {
    if (window.ResizeObserver || imageResizeFrame !== null || !responsiveImages.size) return;
    imageResizeFrame = window.requestAnimationFrame(function () {
      imageResizeFrame = null;
      pruneResponsiveImages();
      var measured = Array.from(responsiveImages).map(function (image) {
        return { image: image, width: slotWidth(image) };
      });
      measured.forEach(function (entry) { setImageSizes(entry.image, entry.width); });
    });
  }, { passive: true });

  function updatePrimaryImage(image, source) {
    if (!image || !source) return;

    trackImageSizes(image);
    var srcset = imageSrcset(source);
    image.setAttribute('src', imageUrlAtWidth(source, 900));
    image.setAttribute('srcset', srcset);
    ['data-src', 'data-original', 'data-lazy-src'].forEach(function (attribute) {
      if (image.hasAttribute(attribute)) image.setAttribute(attribute, imageUrlAtWidth(source, 900));
    });
    if (image.hasAttribute('data-srcset')) image.setAttribute('data-srcset', srcset);

    var picture = image.closest('picture');
    if (picture) {
      picture.querySelectorAll('source').forEach(function (sourceElement) {
        sourceElement.setAttribute('srcset', srcset);
        if (sourceElement.hasAttribute('data-srcset')) {
          sourceElement.setAttribute('data-srcset', srcset);
        }
      });
    }
  }

  function fixCardImages(card, variant) {
    var images = Array.from(
      card.querySelectorAll(
        'a[href*="/products/"] img, .snize-product-image img, img.snize-item-image'
      )
    ).filter(function (image, index, allImages) {
      return allImages.indexOf(image) === index;
    });

    if (!images.length) return 'no card image found';

    var primary =
      images.find(function (image) {
        return !/(hover|secondary|second)/i.test(image.className || '');
      }) || images[0];
    primary.setAttribute('data-snize-variant-primary', 'true');
    // INP fix (2026-09-08): this class is in the MutationObserver's attributeFilter
    // (see observeRoot below). classList.add() rewrites the class attribute even when
    // the token is already present, which queued a mutation record, which re-armed the
    // 80ms schedule() debounce -- a self-sustaining 12.5Hz reflow loop for the life of
    // the page. Only write when the token is actually missing, matching the guarded
    // href write in rewriteCardLinks().
    if (!card.classList.contains('snize-variant-image-fixed')) {
      card.classList.add('snize-variant-image-fixed');
    }

    images.forEach(function (image) {
      if (image !== primary) image.setAttribute('data-snize-variant-hover-hidden', 'true');
    });

    var imageUrl = variantImageUrl(variant);
    if (imageUrl) {
      updatePrimaryImage(primary, imageUrl);

      card.querySelectorAll('*').forEach(function (element) {
        Array.from(element.attributes || []).forEach(function (attribute) {
          if (
            /(hover|secondary|second).*(src|image)|(src|image).*(hover|secondary|second)/i.test(
              attribute.name
            ) &&
            /(?:https?:)?\/\/|\/cdn\/|\/files\//i.test(attribute.value)
          ) {
            element.setAttribute(attribute.name, imageUrlAtWidth(imageUrl, 900));
          }
        });
      });
    }
    return imageUrl ? 'variant image applied; secondary hover images suppressed' : 'secondary hover images suppressed';
  }

  function urlWithVariant(value, handle, variantId) {
    try {
      var url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || handleFromUrl(url.href) !== handle) return '';
      url.searchParams.set('variant', String(variantId));
      return url.pathname + url.search + url.hash;
    } catch (error) {
      return '';
    }
  }

  function rewriteCardLinks(card, handle, variantId) {
    var rewritten = 0;

    card.querySelectorAll('a[href]').forEach(function (anchor) {
      var nextUrl = urlWithVariant(anchor.getAttribute('href'), handle, variantId);
      if (!nextUrl) return;
      if (anchor.getAttribute('href') !== nextUrl) {
        anchor.setAttribute('href', nextUrl);
        rewritten += 1;
      }
    });

    card.querySelectorAll('[data-url], [data-href], [data-product-url]').forEach(function (element) {
      ['data-url', 'data-href', 'data-product-url'].forEach(function (attribute) {
        if (!element.hasAttribute(attribute)) return;
        var nextUrl = urlWithVariant(element.getAttribute(attribute), handle, variantId);
        if (nextUrl) element.setAttribute(attribute, nextUrl);
      });
    });

    return rewritten;
  }

  function cardFingerprint(card) {
    return intentSources(card).join('|') + '|' + domVariantIds(card).join(',');
  }

  function isResultCard(card) {
    if (!card || !card.closest(ROOT_SELECTOR)) return false;
    return !card.closest('.snize-recommendation, [class*="recommendation"]');
  }

  function reapplyCardResult(card, result) {
    if (!result || !result.variant) return result;
    rewriteCardLinks(card, result.handle, result.variantId);
    fixCardImages(card, result.variant);
    card.setAttribute('data-snize-variant-fixed', String(result.variantId));
    return result;
  }

  function processCard(card) {
    var fingerprint = cardFingerprint(card);
    var previous = cardStates.get(card);
    if (previous && previous.fingerprint === fingerprint) {
      return previous.promise.then(function (result) {
        return reapplyCardResult(card, result);
      });
    }

    var handle = getCardHandle(card);
    if (!handle) {
      debug('failure: product handle not found', card);
      return Promise.resolve(null);
    }

    var promise = getProduct(handle)
      .then(function (product) {
        var lengthIndex = findLengthIndex(product);
        if (lengthIndex === -1) {
          throw new Error('Length option not found');
        }

        var lengthMatch = detectLength(card, product, lengthIndex);
        var selection = chooseVariant(card, handle, product, lengthIndex, lengthMatch);
        if (!selection || !selection.variant) {
          throw new Error('No unambiguous variant match');
        }

        var variantId = selection.variant.id;
        var rewritten = rewriteCardLinks(card, handle, variantId);
        var imageResult = fixCardImages(card, selection.variant);
        card.setAttribute('data-snize-variant-fixed', String(variantId));

        debug('card matched', {
          handle: handle,
          length: lengthMatch ? lengthMatch.display : '(direct ID)',
          variantId: variantId,
          strategy: selection.strategy,
          linksRewritten: rewritten,
          hover: imageResult
        });

        return { handle: handle, variantId: variantId, variant: selection.variant };
      })
      .catch(function (error) {
        cardStates.delete(card);
        debug('failure', handle, error.message, card);
        return null;
      });

    cardStates.set(card, { fingerprint: fingerprint, promise: promise });
    return promise;
  }

  function cardsInScope(scope) {
    if (!scope) return [];
    var cards = [];
    if (scope.matches && scope.matches(CARD_SELECTOR) && isResultCard(scope)) cards.push(scope);
    if (scope.querySelectorAll) {
      cards = cards.concat(
        Array.from(scope.querySelectorAll(CARD_SELECTOR)).filter(function (card) {
          return isResultCard(card);
        })
      );
    }
    return unique(cards);
  }

  function processScope(scope) {
    pruneResponsiveImages();
    var cards = cardsInScope(scope || document);
    debug('cards found', cards.length);
    cards.forEach(processCard);
  }

  function schedule(scope) {
    pendingScopes.add(scope || document);
    window.clearTimeout(timer);
    timer = window.setTimeout(function () {
      var scopes = Array.from(pendingScopes);
      pendingScopes.clear();
      scopes.forEach(processScope);
    }, DEBOUNCE_MS);
  }

  function observeRoot(root) {
    if (!root || observedRoots.has(root)) return;
    observedRoots.add(root);

    var observer = new MutationObserver(function () {
      schedule(root);
    });
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['href', 'class', 'aria-selected', 'data-variant-id', 'data-selected-variant-id']
    });

    schedule(root);
  }

  function discoverRoots(scope) {
    if (!scope || !scope.querySelectorAll) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) observeRoot(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(observeRoot);
  }

  function indexApiRecords(data) {
    var arrays = [];
    if (data && Array.isArray(data.items)) arrays.push(data.items);
    if (data && Array.isArray(data.products)) arrays.push(data.products);
    if (data && Array.isArray(data.results)) arrays.push(data.results);

    arrays.forEach(function (items) {
      items.forEach(function (item) {
        if (!item || typeof item !== 'object') return;
        var handle =
          handleFromUrl(item.link || item.url || item.product_url) ||
          String(item.handle || item.product_handle || '').toLowerCase();
        var variantId =
          item.selected_variant_id || item.variant_id || item.add_to_cart_id || item.variantId;
        if (!handle || !numericId(variantId)) return;

        var records = apiRecordsByHandle.get(handle) || [];
        var record = {
          variantId: String(variantId),
          title: item.title || item.product_title || item.name || ''
        };
        var duplicate = records.some(function (existing) {
          return (
            existing.variantId === record.variantId &&
            normalize(existing.title) === normalize(record.title)
          );
        });
        if (!duplicate) records.push(record);
        if (records.length > 100) records.splice(0, records.length - 100);
        apiRecordsByHandle.set(handle, records);
      });
    });
  }

  function bindSearchaniseEvents() {
    if (
      searchaniseEventsBound ||
      !window.Searchanise ||
      typeof window.Searchanise.$ !== 'function'
    ) {
      return;
    }

    searchaniseEventsBound = true;
    var $ = window.Searchanise.$;

    $(document).on('Searchanise.ResultsUpdated.snizeVariantFix', function (event, container) {
      var element = container && (container[0] || container);
      if (element) {
        discoverRoots(element);
        schedule(element);
      } else {
        discoverRoots(document);
        schedule(document);
      }
    });

    $(document).on(
      'Searchanise.AutocompleteUpdated.snizeVariantFix',
      function (event, input, container) {
        var element = container && (container[0] || container);
        if (element) {
          discoverRoots(element);
          schedule(element);
        }
      }
    );

    $(document).on(
      'Searchanise.DataRequestEnd.snizeVariantFix',
      function (event, type, params, data) {
        if (type !== 'results' && type !== 'autocomplete') return;
        indexApiRecords(data);
        schedule(document);
      }
    );

    debug('Searchanise event hooks bound');
  }

  function plainProductClick(event) {
    return (
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    );
  }

  document.addEventListener(
    'click',
    function (event) {
      if (!plainProductClick(event)) return;
      var anchor = event.target.closest && event.target.closest(PRODUCT_LINK_SELECTOR);
      var card = anchor && anchor.closest(CARD_SELECTOR);
      if (!card || !isResultCard(card)) return;

      var originalUrl = anchor.href;
      event.preventDefault();
      event.stopImmediatePropagation();
      processCard(card).then(function (result) {
        var destination = result ? anchor.href : originalUrl;
        if (destination) window.location.assign(destination);
      });
    },
    true
  );

  document.addEventListener(
    'pointerover',
    function (event) {
      var card = event.target.closest && event.target.closest(CARD_SELECTOR);
      if (!card || !isResultCard(card) || hoverRepairPending.has(card)) return;
      if (event.relatedTarget && card.contains(event.relatedTarget)) return;

      var state = cardStates.get(card);
      if (!state) return;
      hoverRepairPending.add(card);

      window.setTimeout(function () {
        state.promise
          .then(function (result) {
            reapplyCardResult(card, result);
          })
          .finally(function () {
            hoverRepairPending.delete(card);
          });
      }, 0);
    },
    true
  );

  document.addEventListener('Searchanise.Loaded', function () {
    bindSearchaniseEvents();
    discoverRoots(document);
    schedule(document);
  });

  function init() {
    bindSearchaniseEvents();
    discoverRoots(document);
    schedule(document);

    var discoveryScope =
      resultsPageEligible && (document.querySelector('#MainContent') || document.body);
    if (discoveryScope) {
      var discoveryObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType === 1) discoverRoots(node);
          });
        });
      });
      discoveryObserver.observe(discoveryScope, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.SearchaniseVariantFix = {
    processResults: function () {
      discoverRoots(document);
      schedule(document);
    },
    clearProductCache: function () {
      productCache.clear();
      debug('product JSON cache cleared');
    }
  };
})();
